import React, { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PanelSectionContent, PanelSubHeader, DefaultFlexStrechedWrapper } from 'src/components/ui/odfjellPanel/OdfjellPanel.styles';
import { ReportOptionCheckbox, ReportOptionDescriptionText, ReportOptionIconWrapper, ReportOptionWrapper } from '../PageHeaderStowage.style';
import { REPORT_TYPE, REPORT_TYPE_FILE_FORMAT, ReportSubType, ReportTypes } from 'src/utils/reportGenerator/ReportGeneratorTypes';
import LastCargoesNumberSelector from './LastCargoesNumberSelector';
import CargoNameTypeSelector from './CargoNameTypeSelector';
import ManualCargoesOnlySelector from './ManualCargoesOnlySelector';
import Tooltip from 'src/components/ui/tooltip/Tooltip';
import { ReportTypesGrid, ReportTypeColumn, SectionDivider, OutputActionLabel, OutputActionsContainer, RadioInput } from './reportPanel.style';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';

export type OutputAction = 'Open' | 'Print' | 'Download';
export type CargoNameType = 'tradeName' | 'productName';

interface ReportOptionsSectionProps {
    groupedReportTypes: Record<REPORT_TYPE, ReportSubType[]>;
    selectedReports: Set<ReportSubType>;
    toggleReportSelection: (reportSubType: ReportSubType) => void;
    selectedReportFileType: string;
    lastCargoesNumberOfSteps: number;
    setLastCargoesNumberOfSteps: (steps: number) => void;
    selectedOutputAction: OutputAction;
    setSelectedOutputAction: (action: OutputAction) => void;
    cargoNameType?: CargoNameType;
    setCargoNameType: (type: CargoNameType) => void;
    manualCargoesOnly: boolean;
    setManualCargoesOnly: (manualCargoesOnly: boolean) => void;
    tanksWithManualCargoes: number[];
    selectedStowageRotationPortCall: RotationPortCall | null;
}

const ReportOptionsSection: React.FC<ReportOptionsSectionProps> = ({
    groupedReportTypes,
    selectedReports,
    toggleReportSelection,
    selectedReportFileType,
    lastCargoesNumberOfSteps,
    setLastCargoesNumberOfSteps,
    selectedOutputAction,
    setSelectedOutputAction,
    cargoNameType = 'tradeName',
    setCargoNameType,
    manualCargoesOnly,
    setManualCargoesOnly,
    tanksWithManualCargoes,
    selectedStowageRotationPortCall,
}) => {
    // Auto-select Download option when xlsx is selected, and return to Open when PDF is selected
    useEffect(() => {
        if (selectedReportFileType === 'xlsx') {
            setSelectedOutputAction('Download');
        } else if (selectedReportFileType === 'pdf' && selectedOutputAction === 'Download') {
            setSelectedOutputAction('Open');
        }
    }, [selectedReportFileType, setSelectedOutputAction]);

    const handleOutputActionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedOutputAction(event.target.value as OutputAction);
    };

    // Determine if xlsx type report is selected to control radio button states
    const isXlsxSelected = selectedReportFileType === 'xlsx';

    const reportTypeEntries = Object.entries(groupedReportTypes);

    const renderReportTypeGroup = (reportTypeKey: string, subTypes: ReportSubType[]) => {
        const reportTypeObject = ReportTypes.find((type) => type.reportType === reportTypeKey);

        return (
            <PanelSectionContent key={reportTypeKey}>
                <PanelSubHeader noBorder>{reportTypeObject?.title || reportTypeKey}</PanelSubHeader>

                {subTypes.map((option) => (
                    <React.Fragment key={option.subType}>
                        <ReportOption
                            option={option}
                            isSelected={selectedReports.has(option)}
                            toggleSelection={toggleReportSelection}
                            isDisabled={
                                (selectedReportFileType !== null && selectedReportFileType !== REPORT_TYPE_FILE_FORMAT[option.reportType]) ||
                                (selectedReportFileType === 'xlsx' && !selectedReports.has(option))
                            }
                        />{' '}
                        {option.reportType === REPORT_TYPE.LAST_CARGOES && (
                            <div style={{ paddingLeft: '18px' }}>
                                <LastCargoesNumberSelector
                                    lastCargoesNumberOfSteps={lastCargoesNumberOfSteps}
                                    setLastCargoesNumberOfSteps={setLastCargoesNumberOfSteps}
                                    disabled={!selectedReports.has(option)}
                                />
                                <Tooltip
                                    text={
                                        tanksWithManualCargoes.length === 0
                                            ? `There are no manually inserted cargoes for this call: ${
                                                  selectedStowageRotationPortCall?.portDisplayName || ''
                                              }.`
                                            : `When selected, only manually inserted cargoes at ${
                                                  selectedStowageRotationPortCall?.portDisplayName || ''
                                              } will be included in the report.`
                                    }
                                    type="information"
                                    disabled={!selectedReports.has(option)}
                                >
                                    <ManualCargoesOnlySelector
                                        manualCargoesOnly={manualCargoesOnly}
                                        setManualCargoesOnly={setManualCargoesOnly}
                                        disabled={!selectedReports.has(option) || !tanksWithManualCargoes.length}
                                    />
                                </Tooltip>
                                <CargoNameTypeSelector
                                    cargoNameType={cargoNameType}
                                    setCargoNameType={setCargoNameType}
                                    disabled={!selectedReports.has(option)}
                                />
                            </div>
                        )}
                    </React.Fragment>
                ))}
            </PanelSectionContent>
        );
    };

    return (
        <>
            <PanelSectionContent>
                <ReportTypesGrid>
                    {reportTypeEntries.map(([reportTypeKey, subTypes]) => (
                        <ReportTypeColumn key={reportTypeKey}>{renderReportTypeGroup(reportTypeKey, subTypes)}</ReportTypeColumn>
                    ))}
                </ReportTypesGrid>
            </PanelSectionContent>

            <PanelSectionContent>
                <SectionDivider />
                <OutputActionsContainer>
                    <OutputActionLabel disabled={isXlsxSelected}>
                        <RadioInput
                            type="radio"
                            name="outputAction"
                            value="Open"
                            checked={selectedOutputAction === 'Open'}
                            onChange={handleOutputActionChange}
                            disabled={isXlsxSelected}
                        />
                        Open
                    </OutputActionLabel>
                    <OutputActionLabel disabled={isXlsxSelected}>
                        <RadioInput
                            type="radio"
                            name="outputAction"
                            value="Print"
                            checked={selectedOutputAction === 'Print'}
                            onChange={handleOutputActionChange}
                            disabled={isXlsxSelected}
                        />
                        Print
                    </OutputActionLabel>
                    <OutputActionLabel>
                        <RadioInput
                            type="radio"
                            name="outputAction"
                            value="Download"
                            checked={selectedOutputAction === 'Download' || isXlsxSelected}
                            onChange={handleOutputActionChange}
                        />
                        Download
                    </OutputActionLabel>
                </OutputActionsContainer>
            </PanelSectionContent>
        </>
    );
};

interface ReportOptionProps {
    option: ReportSubType;
    isSelected: boolean;
    toggleSelection: (option: ReportSubType) => void;
    isDisabled: boolean;
}

const ReportOption: React.FC<ReportOptionProps> = ({ option, isSelected, toggleSelection, isDisabled }) => {
    return (
        <ReportOptionWrapper onClick={() => !isDisabled && toggleSelection(option)} disabled={isDisabled}>
            <DefaultFlexStrechedWrapper noMargin>
                <ReportOptionIconWrapper>
                    <FontAwesomeIcon className="icon" icon={option.icon} />
                    <ReportOptionCheckbox
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelection(option)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={isDisabled}
                        aria-label={`Select ${option.subType}`}
                    />
                </ReportOptionIconWrapper>
                <ReportOptionDescriptionText>{option.subType}</ReportOptionDescriptionText>
            </DefaultFlexStrechedWrapper>
        </ReportOptionWrapper>
    );
};

export default ReportOptionsSection;
